
import { computed, defineComponent, nextTick, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import MappingBlocks from "@/components/list/parts/MappingBlocks.vue";
import AppliedFilters from "@/components/list/parts/AppliedFilters.vue";
import DataTable from "@/components/list/parts/DataTable.vue";
import DataGridWithLeagues from '@/components/list/parts/DataGridWithLeagues.vue'
import PaginationBar from "@/components/list/parts/PaginationBar.vue";
import RowDetailsModal from "@/components/modals/RowDetailsModal.vue";
import Dropdown1 from "@/components/dropdown/Dropdown1.vue";
import Popper from "vue3-popper";
import ApiService from "@/core/services/ApiService";
import Messages from "@/core/helpers/Messages";
import { Modal } from "bootstrap";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default defineComponent({
  name: "TableWidget",
  components: { MappingBlocks, AppliedFilters, DataTable, DataGridWithLeagues, PaginationBar, RowDetailsModal, Dropdown1, Popper },
  props: {
    widgetClasses: String,
    routeKey: String,
  },
  setup(props) {
     const store = useStore();
    const route = useRoute();

    const mapping = computed(() => store.getters.getMapping({ key: props.routeKey }));
    const items = computed(() => store.getters.getDynamicList({ key: props.routeKey }));
    const pagination = computed(() => store.getters.getPaginationParams({ key: props.routeKey }));
    const sortBy = computed(() => store.getters.getSortBy({ key: props.routeKey }));
    const appliedFilters = computed(() => store.getters.getAppliedFilterValues({ key: props.routeKey }));
    const getFiltersMapping = computed(() => store.getters.getFiltersMapping({ key: props.routeKey }));
    const pageName = computed(() => mapping.value ? mapping.value.map.Plural : "");
    const multipleFormEdit = computed(() => store.getters.getMultipleFormEdit({ key: props.routeKey }));
    const getSearchTerm = computed(() => store.getters.getSearchTerm({ key: props.routeKey }));
    const getInlineSearch = computed(() => store.getters.getInlineSearch({ key: props.routeKey }));
    const rowModalConfig = ref<{ title: string; fields: Array<{ label: string; key: string; format?: (data: any) => string }> } | null>(null);
    const rowModalData = ref<any | null>(null);
    const rowModalLoading = ref(false);
    const rowModalId = computed(() => `kt_modal_row_details_${props.routeKey}`);
    const localSearchTerm = ref(
      route.query
        ? route.query["SearchTerm"]
          ? route.query["SearchTerm"]
          : ""
        : ""
    );
    const typingTimer = ref<number | null>(null);
    const TYPING_DELAY = 300;

    const onSort = (columnName: string) => {
      const current = sortBy.value;
      const nextType = current?.sortColumn === columnName && current?.sortType === "asc" ? "desc" : "asc";
      store.dispatch(Actions.SET_SORT_BY, {
        sortBy: { sortColumn: columnName, sortType: nextType },
        key: props.routeKey,
      });
      store.dispatch(Actions.GET_DYNAMIC_LIST, { key: props.routeKey, page: 1 });
    };

    const onOpenModal = (id: number) => {
      store.dispatch(Actions.SETUP_FORM_COMPONENT_OR_MODAL, { key: props.routeKey, id });
    };

    const confirmDelete = async () => {
      const result = await Swal.fire({
        text: "Are you sure you want to delete this item?",
        icon: "error",
        buttonsStyling: true,
        showCancelButton: true,
        showConfirmButton: true,
        reverseButtons: true,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn",
        },
      });
      return result.isConfirmed;
    };

    const runWithDeleteLoader = async (action: () => Promise<void>) => {
      Swal.fire({
        title: "Deleting...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      try {
        await action();
      } finally {
        Swal.close();
      }
    };

    const onDelete = async (payload: { id: number; index: number }) => {
      const confirmed = await confirmDelete();
      if (!confirmed) return;
      await runWithDeleteLoader(() =>
        store.dispatch(Actions.DELETE_ITEM, { key: props.routeKey, ...payload })
      );
    };

    const onDisable = (payload: { id: number; index: number }) => {
      store.dispatch(Actions.DISABLE_ITEM, { key: props.routeKey, ...payload });
    };

    const onPaginate = () => {
      store.dispatch(Actions.GET_DYNAMIC_LIST, {
        key: props.routeKey,
        page: pagination.value.currentPage,
      });
    };

    const onMultipleEdit = () => {
      store.dispatch(Actions.SETUP_FORM_COMPONENT_OR_MODAL, {
        key: props.routeKey,
        isMultipleUpdate: true,
      });
    };

    const onRemoveFilter = (key: string, clear?: () => void) => {
      store.dispatch(Actions.CLEAR_FILTER_TAG, { key: props.routeKey, attrKey: key });
      if (clear) clear();
    };

    const searchKeyUp = (e: KeyboardEvent) => {
      if (e && e.key === "Enter") {
        if (typingTimer.value) clearTimeout(typingTimer.value);
        runSearch();
        return;
      }
      if (typingTimer.value) clearTimeout(typingTimer.value);
      typingTimer.value = setTimeout(runSearch, TYPING_DELAY);
    };

    const runSearch = () => {
      if (localSearchTerm.value.length >= 1) {
        store.dispatch(Actions.SET_SEARCH_TERM, {
          searchTerm: localSearchTerm.value,
          key: props.routeKey,
        });
        if (!getInlineSearch.value) {
          store.dispatch(Actions.GET_DYNAMIC_LIST, {
            page: 1,
            key: props.routeKey,
          });
        }
      } else if (getSearchTerm.value !== "") {
        store.dispatch(Actions.SET_SEARCH_TERM, {
          searchTerm: "",
          key: props.routeKey,
        });
        if (!getInlineSearch.value) {
          store.dispatch(Actions.GET_DYNAMIC_LIST, {
            page: 1,
            key: props.routeKey,
          });
        }
      }
    };

    onUnmounted(() => {
      if (typingTimer.value) clearTimeout(typingTimer.value);
    });

    const buildUrlWithQuery = (url: string, q: Record<string, any> | undefined) => {
      if (!q || Object.keys(q).length === 0) return url;
      const qs = new URLSearchParams(q as any).toString();
      return `${url}${url.includes("?") ? "&" : "?"}${qs}`;
    };

    const callRowApi = async (api: any, row: any) => {
      const urlBase = typeof api.endpoint === "function" ? api.endpoint(row) : api.endpoint;
      const params = typeof api.params === "function" ? api.params(row) : (api.params ?? {});
      const body = typeof api.body === "function" ? api.body(row) : api.body;
      const method = (api.method ?? "GET").toUpperCase();

      switch (method) {
        case "GET":
          return ApiService.get(urlBase, "", params);
        case "DELETE":
          return ApiService.delete(buildUrlWithQuery(urlBase, params));
        case "POST":
          return ApiService.post(urlBase, body, params);
        case "PATCH":
          return ApiService.patch(urlBase, body, params);
        case "PUT":
          return (ApiService as any).put?.(urlBase, body, params);
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
    };

    const openRowModal = async () => {
      await nextTick();
      const modalEl = document.getElementById(rowModalId.value);
      if (!modalEl) return;
      const instance = Modal.getOrCreateInstance(modalEl);
      instance.show();
    };

    const onRowAction = async (payload: { button: any; item: any }) => {
      const { button, item } = payload;
      const api = button?.api;
      const modal = button?.modal;

      if (!api && modal) {
        rowModalConfig.value = modal;
        rowModalData.value = item;
        await openRowModal();
        return;
      }

      if (!api) return;

      rowModalLoading.value = true;
      rowModalConfig.value = modal ?? null;
      rowModalData.value = null;

      try {
        const response = await callRowApi(api, item);
        rowModalData.value = response?.data ?? null;

        if (api.successMessage) {
          Messages.showSuccess(api.successMessage);
        }
        if (api.refreshList) {
          store.dispatch(Actions.GET_DYNAMIC_LIST, { key: props.routeKey, page: pagination.value.currentPage });
        }
        if (modal) {
          await openRowModal();
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message;
        const errors = err?.response?.data?.errors ?? { Error: [msg ?? "Request failed."] };
        Messages.showError(errors);
      } finally {
        rowModalLoading.value = false;
      }
    };

    const moveItem = (fromIndex: number, toIndex: number) => {
      const current = [...(items.value || [])];
      if (toIndex < 0 || toIndex >= current.length) return;
      const [moved] = current.splice(fromIndex, 1);
      current.splice(toIndex, 0, moved);
      store.dispatch(Actions.SET_DYNAMIC_LIST, { key: props.routeKey, list: current });
    };

    const onMoveUp = (index: number) => moveItem(index, index - 1);
    const onMoveDown = (index: number) => moveItem(index, index + 1);

    return {
      mapping,
      items,
      pagination,
      sortBy,
      appliedFilters,
      getFiltersMapping,
      pageName,
      multipleFormEdit,
      localSearchTerm,
      searchKeyUp,
      onSort,
      onOpenModal,
      onDelete,
      onPaginate,
      onMultipleEdit,
      onRemoveFilter,
      onMoveUp,
      onMoveDown,
      onRowAction,
      rowModalConfig,
      rowModalData,
      rowModalLoading,
      rowModalId,
    };
  },
});
